import React, { useState, useEffect } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, Pagination, Mousewheel, A11y } from 'swiper'
import { ProjectTeaserWithTracking, ProjectPreview, NavigationArrow } from 'shared'
import classNames from 'classnames'
import { PopoverIcon, JSONFetcher } from 'shared'
import { usePromise } from 'react-use'
import { ProjectSwiperProps, ProjectList, ArrowPaginationProps, NoProjectsHintProps } from './types'

SwiperCore.use([Navigation, Pagination, Mousewheel, A11y])
/**
 * @TODO Refactor the weird construction with dynamic renderers
 */
export const ProjectSwiper = (props: ProjectSwiperProps): JSX.Element => {
  const mounted = usePromise()
  const [isLoading, setIsLoading] = useState(true)
  const [projects, setProjects] = useState<ProjectList>([])
  const id = Math.random().toString(36).substr(2, 9)

  useEffect(() => {
    mounted(JSONFetcher.call<unknown, { data: ProjectList }>({ url: props.apiUrl })).then(({ data }) => {
      setProjects(data)
      setIsLoading(false)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sortedProjects = () => {
    if (props.orderedIds) {
      const orderedIds: number[] = JSON.parse(props.orderedIds)

      return projects.sort((a, b) => {
        const indexA = orderedIds.indexOf(a.id)
        const indexB = orderedIds.indexOf(b.id)

        if (indexA == indexB) {
          // both are -1
          return 0 // keep order from API
        } else if (indexA == -1) {
          return 1 // sort b before a
        } else if (indexB == -1) {
          return -1 // sort a before b
        } else {
          return indexA - indexB // sort based on their index in orderedIds
        }
      })
    } else {
      return projects
    }
  }

  const renderPreviews = () => {
    const numberOfPreviews = window.innerWidth > 450 ? 3 : 1
    return (
      <div className="row">
        {[...Array(numberOfPreviews).keys()].map((i) => (
          <div className="col-md-8" key={i}>
            <ProjectPreview />
          </div>
        ))}
      </div>
    )
  }

  const slides = sortedProjects().map((project, index) => (
    <SwiperSlide key={project.id}>
      <ProjectTeaserWithTracking
        project={project}
        index={index}
        trackingPath="/home-page/featured-projects"
        showDescription={!!props.showDescription}
        progressbarColor={props.progressbarColor}
        className="w-full"
      />
    </SwiperSlide>
  ))

  if (isLoading) return renderPreviews()
  if (slides.length === 0)
    return <NoProjectsHint noProjectsMessage={props.noProjectsMessage} noProjectsPopover={props.noProjectsPopover} />
  if (slides.length === 1) return slides[0]

  const slidesPerView = 3
  const showNavigationArrows = props.pagination === 'chevron' && slides.length > slidesPerView

  return (
    <div className="relative">
      {props.pagination === 'arrow-buttons' && <ArrowPagination id={id} projectsCount={projects.length} />}
      <div className={classNames('generic-teaser-swiper select-none', props.pagination === 'chevron' && 'px-12')}>
        {showNavigationArrows && (
          <div className="swiper-navigation swiper-navigation-left">
            <NavigationArrow id={`featured-list-${id}-prev`} direction="left" />
          </div>
        )}

        <Swiper
          className="pb-0"
          spaceBetween={30}
          slidesPerView={1}
          pagination={{ clickable: true }}
          navigation={{
            nextEl: `#featured-list-${id}-next`,
            prevEl: `#featured-list-${id}-prev`,
            disabledClass: 'is-disabled',
          }}
          roundLengths
          mousewheel={{ forceToAxis: true }}
          breakpoints={{ 450: { slidesPerView } }}
        >
          {slides}
        </Swiper>

        {showNavigationArrows && (
          <div className="swiper-navigation swiper-navigation-right">
            <NavigationArrow id={`featured-list-${id}-next`} direction="right" />
          </div>
        )}
      </div>
    </div>
  )
}

const NoProjectsHint = ({ noProjectsMessage, noProjectsPopover }: NoProjectsHintProps): JSX.Element | null => {
  if (!noProjectsMessage) return null

  return (
    <>
      <span className="text-gray-700 italic pr-1">{noProjectsMessage}</span>
      <PopoverIcon popover={noProjectsPopover} />
    </>
  )
}

const ArrowPagination = ({ id, projectsCount }: ArrowPaginationProps): JSX.Element | null => {
  if (projectsCount < 4) return null
  return (
    <div className={'btn-group btn-group-with-small-arrows'} style={{ position: 'absolute', top: '-3.4rem', right: 0 }}>
      <a id={`featured-list-${id}-prev`} className="btn btn-secondary">
        <i className="fa fa-caret-left"></i>
      </a>
      <a id={`featured-list-${id}-next`} className="btn btn-secondary">
        <i className="fa fa-caret-right"></i>
      </a>
    </div>
  )
}
